import type { CurrentUser } from '@kanban/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import { ApiError, api, setCsrfToken } from './api.js';
import { clearOfflineCache } from './offline-cache.js';

interface SessionResponse {
  user: CurrentUser;
  csrfToken: string;
}

const SessionContext = createContext<CurrentUser | null>(null);

export function useSessionQuery() {
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        const response = await api.get<SessionResponse>('/auth/me');
        setCsrfToken(response.csrfToken);
        return response.user;
      } catch (error) {
        // 401 ist kein Fehlerzustand, sondern "nicht angemeldet".
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
    /*
     * Beim Start IMMER nachfragen, auch wenn der Stand aus dem
     * Offline-Cache noch frisch aussieht (SPEC 9.7).
     *
     * Der abgelegte Stand ueberlebt das Ende der Sitzung auf dem Server —
     * abgemeldet, abgelaufen oder deaktiviert. Ohne diese Zeile zeigte die
     * App danach bis zu einer Minute die Boards aus dem Cache, statt zur
     * Anmeldung zu gehen. Angezeigt wird trotzdem sofort der abgelegte
     * Stand; die Rueckfrage laeuft daneben. Genau das ist der Punkt: offline
     * bleibt das Board lesbar (A12), online wird binnen eines Aufrufs
     * korrigiert.
     */
    refetchOnMount: 'always',
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { identifier: string; password?: string; pin?: string }) =>
      api.post<SessionResponse>('/auth/login', input),
    onSuccess: (response) => {
      setCsrfToken(response.csrfToken);
      /*
       * Alles ausser der Sitzung selbst verwerfen — im Speicher und auf der
       * Platte. Auf einem Familiengeraet wechselt der Nutzer, nicht das
       * Geraet: ohne das laege Lenas Board weiter in Georgs IndexedDB und
       * waere ihm beim naechsten Start kurz zu sehen.
       *
       * Die Sitzungsabfrage bleibt bewusst stehen. `clear()` nimmt dem
       * angemeldeten Beobachter die Abfrage unter den Fuessen weg; das
       * anschliessende `setQueryData` legt eine NEUE an, von der er nichts
       * mehr erfaehrt — die Anmeldemaske bliebe stehen.
       */
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
      void clearOfflineCache();
      queryClient.setQueryData(['session'], response.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      setCsrfToken(null);
      queryClient.clear();
      void clearOfflineCache();
      queryClient.setQueryData(['session'], null);
    },
  });
}

export function SessionProvider({ user, children }: { user: CurrentUser; children: ReactNode }) {
  return <SessionContext.Provider value={user}>{children}</SessionContext.Provider>;
}

export function useCurrentUser(): CurrentUser {
  const user = useContext(SessionContext);
  if (!user) throw new Error('Kein angemeldeter Nutzer im Kontext');
  return user;
}
